/**
 * SkillTreeNode — a card representing a curriculum node.
 *
 * Shows the node's title, current tier, mastery progress, and a "pin focus"
 * action. Locked nodes show prerequisites instead of an action.
 */

import { type ReactNode, useState } from "react";
import { ProgressBar } from "./ProgressBar.js";
import { ForgettingCurve } from "./ForgettingCurve.js";
import type { ReviewPoint } from "@music-app/learning-system";

export type NodeTier = "locked" | "available" | "introduced" | "practicing" | "mastered";
export type CardState = "new" | "learning" | "review" | "relearning";

export interface ItemDetail {
  id: string;
  label: string;
  stability: number;
  due: string | null;
  lapses: number;
  state: CardState;
  /** Loaded lazily when the node's Details panel is first expanded. */
  reviewHistory?: ReviewPoint[];
}

export interface SkillTreeNodeProps {
  id: string;
  title: string;
  track: string;
  tier: NodeTier;
  mastered: number;
  practicing: number;
  introduced: number;
  total: number;
  /** True when this node is the user's currently-pinned focus. */
  isFocus?: boolean;
  /** Names of unmet prerequisite nodes. Shown when tier === "locked". */
  prereqNames?: string[];
  onPinFocus?: () => void;
  /**
   * Render path for "open the lesson for this node" — typically
   * `/learn/<nodeId>`. When provided, the tile renders a small Lesson
   * link/chip alongside the focus action. Per phase-2-spec §6.5,
   * locked nodes still surface the link if a lesson exists.
   */
  lessonHref?: string;
  /**
   * Render path for "drill this node" — e.g. `/drill/scales`. When
   * provided and the Details panel is expanded, renders a "Drill this →"
   * link at the top of the panel. Only nodes with a matching drill route
   * should pass this prop.
   */
  drillHref?: string;
  /** Per-item detail rows. When provided, a Details toggle appears in the footer. */
  items?: ItemDetail[];
  /** The mastered-threshold stability in days (from MASTERY_PRESETS), for the stability bar. */
  masteredThreshold?: number;
  /** Called when the Details panel opens or closes. Use to lazy-load review history. */
  onExpandChange?: (expanded: boolean) => void;
}

export function SkillTreeNode({
  title,
  track,
  tier,
  mastered,
  practicing,
  introduced,
  total,
  isFocus = false,
  prereqNames,
  onPinFocus,
  lessonHref,
  drillHref,
  items,
  masteredThreshold = 30,
  onExpandChange,
}: SkillTreeNodeProps): ReactNode {
  const isLocked = tier === "locked";
  const [expanded, setExpanded] = useState(false);

  // Coverage gaps, surfaced without expanding: configs never drilled, and
  // configs the user is actively forgetting. The tier counts are disjoint, so
  // untouched is whatever the bar segments don't cover.
  const untouched = Math.max(0, total - mastered - practicing - introduced);
  const highLapseCount = items?.filter((i) => i.lapses >= 3).length ?? 0;

  const handleExpandToggle = () => {
    const next = !expanded;
    setExpanded(next);
    onExpandChange?.(next);
  };

  const curveHistory =
    expanded && items
      ? items.find((i) => i.reviewHistory && i.reviewHistory.length > 1)?.reviewHistory
      : undefined;

  return (
    <article
      className={`group p-5 border transition-colors ${
        isLocked
          ? "bg-paperWarm/40 border-ink/10 opacity-60"
          : isFocus
            ? "bg-paper border-accent"
            : "bg-paper border-ink/20"
      }`}
      aria-label={`Skill node: ${title}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-accent">{track}</span>
        <div className="flex items-center gap-2">
          {!isLocked && onPinFocus && (
            <button
              type="button"
              onClick={onPinFocus}
              aria-label={isFocus ? `Unpin ${title}` : `Pin ${title} as focus`}
              className={`transition-all focus-visible:opacity-100 ${
                isFocus
                  ? "text-accent"
                  : "text-inkFaded opacity-0 group-hover:opacity-60"
              }`}
            >
              <PinIcon filled={isFocus} />
            </button>
          )}
          <TierBadge tier={tier} />
        </div>
      </div>
      <h3 className="font-serif italic text-xl font-medium text-ink mb-3">{title}</h3>

      {!isLocked && (
        <div className="mb-4">
          <ProgressBar
            total={total}
            mastered={mastered}
            practicing={practicing}
            introduced={introduced}
            showLabel
          />
          {(untouched > 0 || highLapseCount > 0) && (
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[10px] tracking-wide">
              {untouched > 0 && (
                <span className="text-inkFaded">
                  {untouched} not yet practiced
                </span>
              )}
              {highLapseCount > 0 && (
                <span className="text-danger">
                  {highLapseCount} need work ⚠
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {isLocked && prereqNames && prereqNames.length > 0 && (
        <p className="text-xs text-inkSoft mb-3">
          Requires: <span className="italic">{prereqNames.join(", ")}</span>
        </p>
      )}

      {!isLocked && onPinFocus && (
        <button
          type="button"
          onClick={onPinFocus}
          className={`w-full text-sm py-2 px-3 transition-colors ${
            isFocus
              ? "bg-accent text-paper border border-accent"
              : "bg-transparent border border-ink/20 hover:bg-ink hover:text-paper hover:border-ink"
          }`}
        >
          {isFocus ? "Pinned as focus" : "Pin as focus"}
        </button>
      )}

      {lessonHref && (
        <a
          href={lessonHref}
          className="block mt-2 text-center text-xs font-mono uppercase tracking-widest text-inkSoft hover:text-accent transition-colors"
        >
          {isLocked ? "Lesson available →" : "Read the lesson →"}
        </a>
      )}

      {items && items.length > 0 && (
        <>
          <button
            type="button"
            onClick={handleExpandToggle}
            className="mt-3 w-full text-left text-[10px] font-mono uppercase tracking-widest text-inkFaded hover:text-ink transition-colors"
            aria-expanded={expanded}
          >
            {expanded ? "Details ↑" : "Details ↓"}
          </button>
          {expanded && (
            <>
              {drillHref && (
                <div className="pt-2 pb-1">
                  <a
                    href={drillHref}
                    className="font-mono text-[9px] uppercase tracking-widest text-inkFaded hover:text-accent transition-colors"
                  >
                    Drill this →
                  </a>
                </div>
              )}
              {curveHistory && (
                <div className="pt-2 pb-2 overflow-hidden">
                  <ForgettingCurve reviews={curveHistory} width={180} height={52} horizonDays={30} />
                </div>
              )}
              <ul className="mt-1 space-y-1.5" aria-label="Item details">
                {items.map((item) => (
                  <ItemDetailRow key={item.id} item={item} masteredThreshold={masteredThreshold} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </article>
  );
}

function ItemDetailRow({
  item,
  masteredThreshold,
}: {
  item: ItemDetail;
  masteredThreshold: number;
}): ReactNode {
  const isNew = item.state === "new";
  const stabilityPct = isNew ? 0 : Math.min(item.stability / masteredThreshold, 1);

  const tierColor =
    item.stability >= masteredThreshold
      ? "bg-accent"
      : item.stability >= masteredThreshold * 0.23
        ? "bg-success"
        : item.stability > 0
          ? "bg-inkSoft"
          : "bg-inkFaded";

  const nextReview = (() => {
    if (isNew) return "new";
    if (!item.due) return "—";
    const diff = Math.round(
      (new Date(item.due).getTime() - Date.now()) / 86_400_000,
    );
    if (diff <= 0) return "due now";
    if (diff === 1) return "tomorrow";
    return `in ${diff} days`;
  })();

  return (
    <li className="py-1.5 border-b border-ink/5 last:border-0">
      <div className="flex items-center gap-2 text-[10px] font-mono">
        <span className="flex-1 min-w-0 truncate text-ink" title={item.label}>
          {item.label}
        </span>
        <div className="w-12 h-1.5 bg-ink/10 rounded-full overflow-hidden flex-shrink-0">
          <div
            className={`h-full rounded-full ${tierColor}`}
            style={{ width: `${stabilityPct * 100}%` }}
          />
        </div>
        <span className="text-inkFaded tabular-nums flex-shrink-0">
          {isNew ? "—" : `${Math.round(item.stability)} d`}
        </span>
      </div>
      <div className="flex gap-3 mt-0.5 text-[10px] font-mono text-inkFaded">
        <span>{nextReview}</span>
        <span
          className={item.lapses >= 3 ? "text-danger" : ""}
          aria-label={`${item.lapses} ${item.lapses === 1 ? "lapse" : "lapses"}`}
        >
          {item.lapses} {item.lapses === 1 ? "lapse" : "lapses"}
          {item.lapses >= 3 && <span aria-hidden> ⚠</span>}
        </span>
      </div>
    </li>
  );
}

function PinIcon({ filled }: { filled: boolean }): ReactNode {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      <circle
        cx="5"
        cy="3.5"
        r="2.5"
        fill={filled ? "currentColor" : "none"}
        stroke={filled ? "none" : "currentColor"}
        strokeWidth="1.5"
      />
      <line x1="5" y1="6" x2="5" y2="9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function TierBadge({ tier }: { tier: NodeTier }): ReactNode {
  const labels: Record<NodeTier, string> = {
    locked: "locked",
    available: "available",
    introduced: "introduced",
    practicing: "practicing",
    mastered: "mastered",
  };
  const colors: Record<NodeTier, string> = {
    locked: "text-inkFaded",
    available: "text-inkSoft",
    introduced: "text-warning",
    practicing: "text-info",
    mastered: "text-success",
  };
  return (
    <span className={`font-mono text-[10px] uppercase tracking-wider ${colors[tier]}`}>
      {labels[tier]}
    </span>
  );
}
